/* Daily Journey helpers, local-only (no auth).
 *
 * Extends the app with:
 *   - Devotional completion (per-day)
 *   - Favorites (devotionals, prayers, Bible passage references)
 *   - Share (Web Share API with clipboard fallback)
 *   - Action of the Day (rotating prompt, falls back to devotional.application)
 *   - Biblical connections (3 refs per weekday, pointing to bundled chapters)
 */

import { api } from "./api";

// ---------- Storage keys ----------
const COMPLETIONS_KEY = "cm_devotional_completions_v1"; // { "2026-04-27": true, ... }
const FAVORITES_KEY = "cm_favorites_v1"; // { devotionals:[], prayers:[], passages:[] }

// ---------- Safe storage helpers ----------
const safeRead = (key) => {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const safeWrite = (key, value) => {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota, ignore */
  }
};

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// ---------- Completions ----------
const readCompletions = () => {
  const raw = safeRead(COMPLETIONS_KEY);
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
};

export const isTodayCompleted = () => !!readCompletions()[todayKey()];

export const markTodayCompleted = () => {
  const all = readCompletions();
  all[todayKey()] = { at: new Date().toISOString() };
  safeWrite(COMPLETIONS_KEY, all);
  // Persistência no servidor (conta autenticada); anônimo falha em silêncio.
  api.post("/journey/devotional-complete").catch(() => {});
};

// Cross-device: consulta o servidor e mescla com o estado local.
export const syncTodayCompletionFromServer = async () => {
  try {
    const r = await api.get("/journey/devotional-status");
    if (r.data?.completed && !isTodayCompleted()) {
      const all = readCompletions();
      all[todayKey()] = { at: new Date().toISOString(), from: "server" };
      safeWrite(COMPLETIONS_KEY, all);
    } else if (isTodayCompleted() && r.data && !r.data.completed) {
      api.post("/journey/devotional-complete").catch(() => {});
    }
    return r.data?.completed || isTodayCompleted();
  } catch {
    return isTodayCompleted();
  }
};

export const unmarkTodayCompleted = () => {
  const all = readCompletions();
  delete all[todayKey()];
  safeWrite(COMPLETIONS_KEY, all);
};

export const getCompletionsCount = () => Object.keys(readCompletions()).length;

/**
 * Últimos 7 dias (mais antigo primeiro, hoje por último) com flag `active`.
 * Usado pelo componente StreakVisual para renderizar a barra semanal na
 * Home anônima (que não bate no backend).
 */
export const getWeekCompletion = () => {
  const map = readCompletions();
  const week = [];
  const now = zeroTime(new Date());
  for (let offset = 6; offset >= 0; offset -= 1) {
    const d = new Date(now.getTime() - offset * 86400000);
    const k = dateKey(d);
    week.push({ date: k, active: !!map[k] });
  }
  return week;
};

// ---------- Streak (daily habit) ----------
const zeroTime = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const dateKey = (d) => {
  const x = zeroTime(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

const fromKey = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return zeroTime(new Date(y, (m || 1) - 1, d || 1));
};

const daysBetween = (a, b) => Math.round((zeroTime(a) - zeroTime(b)) / 86400000);

/** Current streak counted from today (or yesterday if today not yet completed). */
export const getCurrentStreak = () => {
  const map = readCompletions();
  const keys = Object.keys(map);
  if (!keys.length) return 0;

  const today = zeroTime(new Date());
  const todayK = dateKey(today);
  const yesterday = new Date(today.getTime() - 86400000);
  const yesterdayK = dateKey(yesterday);

  if (!map[todayK] && !map[yesterdayK]) return 0;

  let cursor = map[todayK] ? today : yesterday;
  let streak = 0;
  // Hard-cap to avoid pathological loops on corrupted data.
  for (let i = 0; i < 366; i += 1) {
    if (map[dateKey(cursor)]) {
      streak += 1;
      cursor = new Date(cursor.getTime() - 86400000);
    } else {
      break;
    }
  }
  return streak;
};

/** Longest consecutive streak across all history. */
export const getLongestStreak = () => {
  const keys = Object.keys(readCompletions()).sort();
  if (!keys.length) return 0;
  let longest = 1;
  let current = 1;
  for (let i = 1; i < keys.length; i += 1) {
    const prev = fromKey(keys[i - 1]);
    const curr = fromKey(keys[i]);
    if (Number.isFinite(prev.getTime()) && Number.isFinite(curr.getTime()) && daysBetween(curr, prev) === 1) {
      current += 1;
      if (current > longest) longest = current;
    } else {
      current = 1;
    }
  }
  return longest;
};

/** Count of reading plans finalized locally. */
export const getCompletedPlansCount = () => {
  try {
    const raw = window.localStorage.getItem("cm_plan_progress_v1");
    const map = raw ? JSON.parse(raw) : null;
    if (!map || typeof map !== "object" || Array.isArray(map)) return 0;
    return Object.values(map).filter((p) => p && p.completed_at).length;
  } catch {
    return 0;
  }
};

/** Human-friendly banner message. Returns null when there's nothing to say. */
export const getStreakMessage = () => {
  const streak = getCurrentStreak();
  const total = getCompletionsCount();
  if (streak >= 1) {
    return {
      tone: "active",
      streak,
      title: `${streak} ${streak === 1 ? "dia" : "dias"} caminhando com Deus`,
      subtitle: "Sua constância tem sido bênção.",
    };
  }
  if (total > 0) {
    return {
      tone: "gentle",
      streak: 0,
      title: "Comece novamente hoje",
      subtitle: "Toda jornada espiritual se renova em graça.",
    };
  }
  return null;
};

/** Positive feedback after marking today completed. */
export const getCompletionFeedback = () => {
  const streak = getCurrentStreak();
  if (streak <= 1) {
    return {
      title: "Muito bem!",
      description: "Continue amanhã. Um passo de cada vez.",
    };
  }
  if (streak <= 6) {
    return {
      title: "Muito bem!",
      description: `Você está construindo um hábito espiritual, ${streak} dias seguidos.`,
    };
  }
  if (streak <= 20) {
    return {
      title: "Que bênção!",
      description: `${streak} dias caminhando em disciplina espiritual.`,
    };
  }
  return {
    title: "Impressionante constância!",
    description: `${streak} dias com Deus. Persevera.`,
  };
};

// ---------- Favorites ----------
const emptyFavs = () => ({ devotionals: [], prayers: [], passages: [] });

const readFavorites = () => {
  const raw = safeRead(FAVORITES_KEY);
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return emptyFavs();
  return {
    devotionals: Array.isArray(raw.devotionals) ? raw.devotionals : [],
    prayers: Array.isArray(raw.prayers) ? raw.prayers : [],
    passages: Array.isArray(raw.passages) ? raw.passages : [],
  };
};

export const getAllFavorites = () => readFavorites();

const matchById = (arr, id) => arr.some((it) => it.id === id);

export const isFavoriteDevotional = (id) => {
  if (!id) return false;
  return matchById(readFavorites().devotionals, String(id));
};

export const toggleFavoriteDevotional = ({ id, title, verse_ref }) => {
  if (!id) return false;
  const favs = readFavorites();
  const strId = String(id);
  const exists = matchById(favs.devotionals, strId);
  if (exists) {
    favs.devotionals = favs.devotionals.filter((it) => it.id !== strId);
  } else {
    favs.devotionals.unshift({
      id: strId,
      title: title || "",
      verse_ref: verse_ref || "",
      saved_at: new Date().toISOString(),
    });
  }
  safeWrite(FAVORITES_KEY, favs);
  return !exists;
};

export const isFavoritePrayer = (devotionalId) => {
  if (!devotionalId) return false;
  return matchById(readFavorites().prayers, String(devotionalId));
};

export const toggleFavoritePrayer = ({ devotional_id, title, text }) => {
  if (!devotional_id) return false;
  const favs = readFavorites();
  const strId = String(devotional_id);
  const exists = matchById(favs.prayers, strId);
  if (exists) {
    favs.prayers = favs.prayers.filter((it) => it.id !== strId);
  } else {
    favs.prayers.unshift({
      id: strId,
      title: title || "",
      text: text || "",
      saved_at: new Date().toISOString(),
    });
  }
  safeWrite(FAVORITES_KEY, favs);
  return !exists;
};

export const isFavoritePassage = (ref) => {
  if (!ref) return false;
  return readFavorites().passages.some((it) => it.ref === ref);
};

export const toggleFavoritePassage = ({ ref, href }) => {
  if (!ref) return false;
  const favs = readFavorites();
  const exists = favs.passages.some((it) => it.ref === ref);
  if (exists) {
    favs.passages = favs.passages.filter((it) => it.ref !== ref);
  } else {
    favs.passages.unshift({
      ref,
      href: href || "",
      saved_at: new Date().toISOString(),
    });
  }
  safeWrite(FAVORITES_KEY, favs);
  return !exists;
};

// ---------- Share ----------
export const shareContent = async ({ title, text, url }) => {
  const payload = { title: title || "", text: text || "", url: url || window.location.href };
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share(payload);
      return "shared";
    } catch {
      // user cancelled or failure, fall through to clipboard
    }
  }
  try {
    const copyText = [payload.title, payload.text, payload.url].filter(Boolean).join("\n\n");
    await navigator.clipboard.writeText(copyText);
    return "copied";
  } catch {
    return "failed";
  }
};

// ---------- Action of the Day ----------
const DAILY_ACTIONS = [
  "Hoje, pratique gratidão em cada pequeno detalhe.",
  "Reserve um tempo em silêncio diante de Deus.",
  "Ore por alguém específico hoje.",
  "Leia novamente este versículo à noite.",
  "Compartilhe uma palavra de encorajamento com alguém.",
  "Escreva três coisas pelas quais você é grato.",
  "Faça uma obra de bondade discreta.",
];

const dayOfYear = () => {
  const d = new Date();
  const start = new Date(d.getFullYear(), 0, 0);
  return Math.floor((d - start) / 86400000);
};

/** Returns a short actionable phrase for today. Prefers devotional.application when available. */
export const getActionOfTheDay = (devotional) => {
  const fromDevo = devotional?.application?.trim();
  if (fromDevo) return fromDevo;
  return DAILY_ACTIONS[dayOfYear() % DAILY_ACTIONS.length];
};

// ---------- Biblical Connections ----------
/**
 * Weekly rotation of 3 references, all mapped to bundled chapters when possible
 * so the user can tap straight into /biblia.
 */
const CONNECTIONS_BY_WEEKDAY = [
  // Sunday
  [
    { ref: "Salmos 23", book_slug: "salmos", chapter: 23 },
    { ref: "João 14", book_slug: "joao", chapter: 14 },
    { ref: "Romanos 8", book_slug: "romanos", chapter: 8 },
  ],
  // Monday
  [
    { ref: "Salmos 121", book_slug: "salmos", chapter: 121 },
    { ref: "João 3", book_slug: "joao", chapter: 3 },
    { ref: "Romanos 8", book_slug: "romanos", chapter: 8 },
  ],
  // Tuesday
  [
    { ref: "Salmos 1", book_slug: "salmos", chapter: 1 },
    { ref: "João 15", book_slug: "joao", chapter: 15 },
    { ref: "Romanos 8", book_slug: "romanos", chapter: 8 },
  ],
  // Wednesday
  [
    { ref: "Salmos 23", book_slug: "salmos", chapter: 23 },
    { ref: "João 10", book_slug: "joao", chapter: 10 },
    { ref: "Romanos 8", book_slug: "romanos", chapter: 8 },
  ],
  // Thursday
  [
    { ref: "Salmos 121", book_slug: "salmos", chapter: 121 },
    { ref: "João 6", book_slug: "joao", chapter: 6 },
    { ref: "Romanos 8", book_slug: "romanos", chapter: 8 },
  ],
  // Friday
  [
    { ref: "Salmos 1", book_slug: "salmos", chapter: 1 },
    { ref: "João 19", book_slug: "joao", chapter: 19 },
    { ref: "Romanos 8", book_slug: "romanos", chapter: 8 },
  ],
  // Saturday
  [
    { ref: "Salmos 23", book_slug: "salmos", chapter: 23 },
    { ref: "João 21", book_slug: "joao", chapter: 21 },
    { ref: "Romanos 8", book_slug: "romanos", chapter: 8 },
  ],
];

/** Returns 3 references for today with resolved hrefs for bundled chapters. */
export const getDailyConnections = () => {
  const weekday = new Date().getDay();
  const picks = CONNECTIONS_BY_WEEKDAY[weekday] || CONNECTIONS_BY_WEEKDAY[0];
  return picks.map((p) => ({
    ref: p.ref,
    href: p.book_slug && p.chapter ? `/biblia/${p.book_slug}/${p.chapter}` : null,
    available: !!(p.book_slug && p.chapter),
  }));
};

/** AI-driven connections, fetched from /api/assistant/connections.
 *  Falls back to the static weekday rotation if the call fails or returns
 *  source="fallback". Cached in sessionStorage for 60 minutes to avoid
 *  re-fetching on every Home mount within the same browsing session. */
export const fetchAIConnections = async (axiosInstance) => {
  const SESSION_KEY = "cm_ai_connections_v1";
  try {
    const raw = window.sessionStorage?.getItem(SESSION_KEY);
    if (raw) {
      const cached = JSON.parse(raw);
      if (cached?.date_key === new Date().toISOString().slice(0, 10)
          && Array.isArray(cached.items) && cached.items.length === 3) {
        return cached.items;
      }
    }
  } catch { /* ignore */ }

  try {
    const r = await axiosInstance.get("/assistant/connections");
    const data = r?.data;
    if (data?.source === "ai" && Array.isArray(data.connections) && data.connections.length === 3) {
      const items = data.connections.map((c) => ({
        ref: c.ref,
        href: c.verse
          ? `/biblia/${c.book_slug}/${c.chapter}?v=${c.verse}`
          : `/biblia/${c.book_slug}/${c.chapter}`,
        available: true,
      }));
      try {
        window.sessionStorage?.setItem(SESSION_KEY, JSON.stringify({
          date_key: data.date_key, items,
        }));
      } catch { /* ignore */ }
      return items;
    }
  } catch { /* ignore, fall through */ }
  return getDailyConnections();
};
